import { parseDefaultInstruction } from './defaultAnalyzer.js';
import { parseReadInstruction } from './readAnalyzer.js';

const EXECUTABLE_NODE_TYPES = new Set([
  'command',
  'read',
  'default',
  'if',
  'goto',
  'call',
  'exit',
  'remark',
  'ssr',
  'menu',
  'window',
  'showWindow',
  'showMenu',
  'write',
  'compute',
  'require',
]);

const BLOCK_ACTION_NODE_TYPES = new Set([...EXECUTABLE_NODE_TYPES, 'button_definition']);
const CONDITION_PATTERN = /^\s*\[(@?[A-Z0-9_#]+)\]\s*(<>|!=|=|==|<=|>=|<|>)\s*(".*?"|'.*?'|\[@?[A-Z0-9_#]+\]|TRUE|FALSE|[^\s]+)\s*$/i;
const SUFFIX_PATTERN = /^(.*?)(#[A-Z])$/i;

function cloneIr(ir) {
  return JSON.parse(JSON.stringify(ir));
}

function normalizeLabelName(name = '') {
  return name.replace(/:$/, '').trim().toUpperCase();
}

function cleanInlineActionText(text = '') {
  return text.replace(/\b(?:ENDIF|ELSE|THEN)\b.*$/i, '').trim();
}

function cleanActionTarget(expression = '') {
  const match = cleanInlineActionText(expression).match(/^([A-Z0-9_.-]+)/i);

  return match ? match[1].toUpperCase() : '';
}

function getNodeRaw(node) {
  return node.raw || node.normalizedRaw || node.expression || node.text || '';
}

function parseTechnicalSuffix(variableName = '') {
  const match = variableName.match(SUFFIX_PATTERN);

  return match
    ? {
        name: match[1],
        suffix: match[2].toUpperCase(),
      }
    : {
        name: variableName,
        suffix: null,
      };
}

function stripTechnicalSuffix(variableName = '') {
  return parseTechnicalSuffix(variableName).name;
}

function normalizeOperator(operator) {
  if (operator === '==') {
    return '=';
  }

  if (operator === '!=') {
    return '<>';
  }

  return operator;
}

function normalizeConditionValue(rawValue = '') {
  const value = rawValue.trim();

  if (/^\[(@?[A-Z0-9_#]+)\]$/i.test(value)) {
    const variable = value.slice(1, -1);
    const variableInfo = parseTechnicalSuffix(variable);

    return {
      right: variableInfo.name,
      rightType: 'variable',
      rightSuffix: variableInfo.suffix,
    };
  }

  const unquotedValue =
    (value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))
      ? value.slice(1, -1)
      : value;

  if (/^TRUE$/i.test(unquotedValue)) {
    return {
      right: true,
      rightType: 'literal',
    };
  }

  if (/^FALSE$/i.test(unquotedValue)) {
    return {
      right: false,
      rightType: 'literal',
    };
  }

  return {
    right: unquotedValue,
    rightType: 'literal',
  };
}

function parseCondition(conditionText = '') {
  const match = conditionText.match(CONDITION_PATTERN);

  if (!match) {
    return null;
  }

  const left = parseTechnicalSuffix(match[1]);
  const right = normalizeConditionValue(match[3]);

  return {
    left: left.name,
    leftSuffix: left.suffix || undefined,
    operator: normalizeOperator(match[2]),
    ...right,
  };
}

function splitInlineIfExpression(expression = '') {
  const thenMatch = expression.match(/\bTHEN\b/i);

  if (!thenMatch) {
    return {
      conditionText: expression.trim(),
      thenText: '',
      elseText: '',
      hasInlineEndif: /\bENDIF\b/i.test(expression),
    };
  }

  const conditionText = expression.slice(0, thenMatch.index).trim();
  const afterThen = expression.slice(thenMatch.index + thenMatch[0].length).trim();
  const elseMatch = afterThen.match(/\bELSE\b/i);
  const hasInlineEndif = /\bENDIF\b/i.test(afterThen);

  if (elseMatch) {
    return {
      conditionText,
      thenText: afterThen.slice(0, elseMatch.index).replace(/\bENDIF\b.*$/i, '').trim(),
      elseText: afterThen.slice(elseMatch.index + elseMatch[0].length).replace(/\bENDIF\b.*$/i, '').trim(),
      hasInlineEndif,
    };
  }

  return {
    conditionText,
    thenText: afterThen.replace(/\bENDIF\b.*$/i, '').trim(),
    elseText: '',
    hasInlineEndif,
  };
}

function parseConditions(expression = '') {
  const { conditionText } = splitInlineIfExpression(expression);
  const tokens = conditionText.split(/\s+(AND|OR)\s+/i);
  const conditions = [];

  for (let index = 0; index < tokens.length; index += 2) {
    if (index > 0) {
      conditions.push({
        join: tokens[index - 1].toUpperCase(),
      });
    }

    const condition = parseCondition(tokens[index]);

    if (condition) {
      conditions.push(condition);
    }
  }

  return conditions;
}

function extractSabreCommandBody(raw = '') {
  const normalizedRaw = raw.trim().replace(/\u00c2(?=[\u00a4\u00af\u00ae\ufffd])/g, '');

  if (!/^[\u00a4\u00af\ufffd]/.test(normalizedRaw)) {
    return null;
  }

  return normalizedRaw.slice(1).replace(/[\u00ae\u00a4\ufffd]+$/g, '');
}

function parseCommandTemplate(raw = '') {
  const commandBody = extractSabreCommandBody(raw);

  if (!commandBody) {
    return null;
  }

  return commandBody
    .replace(/\{ENTER\}/gi, '')
    .replace(/[\u009d\u00a4\ufffd]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\[(@?[A-Z0-9_#]+)\]/gi, (_match, variableName) => `{${stripTechnicalSuffix(variableName)}}`)
    .trim();
}

function isRemarkTemplate(template = '') {
  return /^(?:5H(?:[-/.])?|5\.|5\*|5A|5U)/i.test(template);
}

function createBaseAction(node, extra = {}) {
  return {
    type: node.type,
    line: node.line,
    raw: node.raw,
    normalizedRaw: node.normalizedRaw,
    ...extra,
  };
}

function createLineActionMap(ir) {
  const map = new Map();

  (ir.logic || []).forEach((action) => {
    if (!action || !action.line) {
      return;
    }

    map.set(action.line, action);
  });

  return map;
}

function parseTextAction(text = '', line = null, raw = text) {
  const cleanText = cleanInlineActionText(text);

  if (!cleanText) {
    return null;
  }

  const commandTemplate = parseCommandTemplate(cleanText);

  if (commandTemplate) {
    if (isRemarkTemplate(commandTemplate)) {
      return {
        type: 'remark',
        template: commandTemplate,
        remarkType: 'Historical',
        skip: true,
        visible: false,
        line,
        raw,
      };
    }

    return {
      type: 'command',
      template: commandTemplate,
      visible: false,
      line,
      raw,
    };
  }

  const commandMatch = cleanText.match(/^(GOTO|CALL|WINDOW|EXIT|WRITE|READ|DEFAULT)\b\s*(.*)$/i);

  if (!commandMatch) {
    return null;
  }

  const keyword = commandMatch[1].toLowerCase();
  const expression = commandMatch[2].trim();

  if (keyword === 'goto') {
    return {
      type: 'goto',
      target: cleanActionTarget(expression),
      expression,
      line,
      raw,
    };
  }

  if (keyword === 'call') {
    return {
      type: 'call',
      target: cleanActionTarget(expression),
      expression,
      line,
      raw,
    };
  }

  if (keyword === 'window') {
    return {
      type: 'showWindow',
      target: cleanActionTarget(expression),
      title: expression.replace(/^ERROR\b/i, '').trim() || expression,
      line,
      raw,
    };
  }

  if (keyword === 'exit') {
    return {
      type: 'exit',
      line,
      raw,
    };
  }

  if (keyword === 'read') {
    return {
      ...parseReadInstruction(cleanText),
      line,
      raw,
    };
  }

  if (keyword === 'default') {
    return {
      ...parseDefaultInstruction(cleanText),
      line,
      raw,
    };
  }

  return {
    type: keyword,
    expression,
    line,
    raw,
  };
}

function normalizeAction(node, logicByLine, context = {}) {
  const logicAction = logicByLine.get(node.line);

  if (logicAction && node.type !== 'default' && node.type !== 'window') {
    const action = {
      ...logicAction,
      raw: logicAction.raw || node.raw,
      normalizedRaw: logicAction.normalizedRaw || node.normalizedRaw,
    };

    if (node.type === 'goto' && !action.target) {
      action.target = cleanActionTarget(action.expression || node.expression);
    }

    if (node.type === 'call' && !action.target) {
      action.target = cleanActionTarget(action.expression || node.expression);
    }

    return action;
  }

  if (node.type === 'command') {
    return createBaseAction(node, {
      type: 'command',
      template: node.lineClassification?.template || parseCommandTemplate(node.normalizedRaw || node.raw) || node.expression || node.raw,
      visible: false,
    });
  }

  if (node.type === 'remark') {
    return createBaseAction(node, {
      type: 'remark',
      remarkType: node.remarkType || 'Historical',
      template: node.template || node.lineClassification?.template || node.text,
      skip: node.skip !== false,
      visible: false,
    });
  }

  if (node.type === 'read') {
    return createBaseAction(node, {
      type: 'read',
      source: node.source,
      row: node.row,
      column: node.column,
      search: node.search,
      target: node.target,
      targets: node.targets || [],
      variables: node.variables || [],
      literals: node.literals || [],
      until: node.until,
      parseStatus: node.parseStatus,
      visible: false,
    });
  }

  if (node.type === 'default') {
    return createBaseAction(node, {
      type: 'default',
      defaultType: node.defaultType || 'unknown',
      target: node.target,
      suffix: node.suffix,
      targetSuffix: node.targetSuffix,
      value: node.value,
      valueSuffix: node.valueSuffix,
      valueType: node.valueType,
      isSelfAssignment: node.isSelfAssignment,
      catalogId: node.catalogId,
      options: node.options,
      parseStatus: node.parseStatus || 'unknown',
      visible: false,
    });
  }

  if (node.type === 'goto') {
    return createBaseAction(node, {
      type: 'goto',
      target: cleanActionTarget(node.expression),
      expression: node.expression,
    });
  }

  if (node.type === 'call') {
    return createBaseAction(node, {
      type: 'call',
      target: cleanActionTarget(node.expression),
      expression: node.expression,
    });
  }

  if (node.type === 'exit') {
    return createBaseAction(node, {
      type: 'exit',
    });
  }

  if (node.type === 'button_definition') {
    return createBaseAction(node, {
      type: 'button_definition',
      button: node.button,
      label: node.label,
      visible: false,
    });
  }

  if (node.type === 'window' || node.type === 'showWindow') {
    return createBaseAction(node, {
      type: context.insideIf ? 'showWindow' : 'window',
      target: node.id,
      formRef: node.id,
      id: node.id,
      title: node.title,
    });
  }

  if (node.type === 'menu') {
    const menu = context.menuByLine?.get(node.line);

    if (menu?.parentKey) {
      return null;
    }

    return createBaseAction(node, {
      type: 'showMenu',
      target: menu?.id || node.id,
      id: menu?.id || node.id,
      title: menu?.title || node.title,
    });
  }

  if (node.type === 'ssr') {
    return createBaseAction(node, {
      type: 'ssr',
      template: node.template || node.lineClassification?.template || node.expression || node.raw,
      visible: false,
    });
  }

  if (['write', 'compute', 'require'].includes(node.type)) {
    return createBaseAction(node, {
      expression: node.expression,
      visible: false,
    });
  }

  return null;
}

function createIfAction(node) {
  return {
    type: 'if',
    conditions: parseConditions(node.expression || ''),
    then: [],
    line: node.line,
    raw: node.raw,
    normalizedRaw: node.normalizedRaw,
  };
}

function groupIf(nodes, index, logicByLine, options = {}) {
  const node = nodes[index];
  const ifAction = createIfAction(node);
  const diagnostics = [];
  const inline = splitInlineIfExpression(node.expression || '');

  if (inline.thenText) {
    const thenAction = parseTextAction(inline.thenText, node.line, node.raw);

    if (thenAction) {
      ifAction.then.push(thenAction);
    }
  }

  if (inline.elseText) {
    const elseAction = parseTextAction(inline.elseText, node.line, node.raw);

    if (elseAction) {
      ifAction.else = [elseAction];
    }
  }

  if (inline.hasInlineEndif || inline.thenText || inline.elseText) {
    return {
      action: ifAction,
      nextIndex: index + 1,
      diagnostics,
    };
  }

  let cursor = index + 1;

  if (nodes[cursor]?.type === 'then') {
    const thenAction = parseTextAction(nodes[cursor].expression || '', nodes[cursor].line, nodes[cursor].raw);

    if (thenAction) {
      ifAction.then.push(thenAction);
    }

    cursor += 1;
  }

  const thenResult = groupActionSequence(nodes, cursor, logicByLine, {
    insideIf: true,
    stopOnIfBoundary: true,
    menuByLine: options.menuByLine,
    menuActionLines: options.menuActionLines,
  });
  ifAction.then.push(...thenResult.actions);
  diagnostics.push(...thenResult.diagnostics);

  if (thenResult.endType === 'else') {
    const elseNode = nodes[thenResult.nextIndex];
    const elseActions = [];
    const inlineElseAction = parseTextAction(elseNode.expression || '', elseNode.line, elseNode.raw);

    if (inlineElseAction) {
      elseActions.push(inlineElseAction);
    }

    const elseResult = groupActionSequence(nodes, thenResult.nextIndex + 1, logicByLine, {
      insideIf: true,
      stopOnIfBoundary: true,
      menuByLine: options.menuByLine,
      menuActionLines: options.menuActionLines,
    });
    elseActions.push(...elseResult.actions);
    diagnostics.push(...elseResult.diagnostics);

    if (elseActions.length > 0) {
      ifAction.else = elseActions;
    }

    if (elseResult.endType === 'endif') {
      return {
        action: ifAction,
        nextIndex: elseResult.nextIndex + 1,
        diagnostics,
      };
    }

    diagnostics.push({
      type: 'diagnostic',
      level: 'error',
      code: 'IF_WITHOUT_ENDIF',
      line: node.line,
      raw: node.raw,
    });

    return {
      action: ifAction,
      nextIndex: elseResult.nextIndex,
      diagnostics,
    };
  }

  if (thenResult.endType === 'endif') {
    return {
      action: ifAction,
      nextIndex: thenResult.nextIndex + 1,
      diagnostics,
    };
  }

  diagnostics.push({
    type: 'diagnostic',
    level: 'error',
    code: 'IF_WITHOUT_ENDIF',
    line: node.line,
    raw: node.raw,
  });

  return {
    action: ifAction,
    nextIndex: thenResult.nextIndex,
    diagnostics,
  };
}

function groupActionSequence(nodes, startIndex, logicByLine, options = {}) {
  const actions = [];
  const diagnostics = [];
  let index = startIndex;

  while (index < nodes.length) {
    const node = nodes[index];

    if (options.menuActionLines?.has(node.line) && node.type !== 'menu') {
      index += 1;
      continue;
    }

    if (node.type === 'endif') {
      if (options.stopOnIfBoundary) {
        return {
          actions,
          diagnostics,
          nextIndex: index,
          endType: 'endif',
        };
      }

      diagnostics.push({
        type: 'diagnostic',
        level: 'warning',
        code: 'ENDIF_WITHOUT_IF',
        line: node.line,
        raw: node.raw,
      });
      index += 1;
      continue;
    }

    if (node.type === 'else') {
      if (options.stopOnIfBoundary) {
        return {
          actions,
          diagnostics,
          nextIndex: index,
          endType: 'else',
        };
      }

      diagnostics.push({
        type: 'diagnostic',
        level: 'warning',
        code: 'ELSE_WITHOUT_IF',
        line: node.line,
        raw: node.raw,
      });
      index += 1;
      continue;
    }

    if (node.type === 'if') {
      const groupedIf = groupIf(nodes, index, logicByLine, {
        menuByLine: options.menuByLine,
        menuActionLines: options.menuActionLines,
      });
      actions.push(groupedIf.action);
      diagnostics.push(...groupedIf.diagnostics);
      index = groupedIf.nextIndex;
      continue;
    }

    if (node.type === 'then') {
      const thenAction = parseTextAction(node.expression || '', node.line, node.raw);

      if (thenAction) {
        actions.push(thenAction);
      }

      index += 1;
      continue;
    }

    if (BLOCK_ACTION_NODE_TYPES.has(node.type)) {
      const action = normalizeAction(node, logicByLine, {
        insideIf: Boolean(options.insideIf),
        menuByLine: options.menuByLine,
      });

      if (action) {
        actions.push(action);
      }
    }

    index += 1;
  }

  return {
    actions,
    diagnostics,
    nextIndex: index,
    endType: 'eof',
  };
}

function hasExecutableActions(block) {
  return (block.actions || []).some((action) => action && EXECUTABLE_NODE_TYPES.has(action.type));
}

function inferBlockType(block) {
  if (block.name === 'BOTONES') {
    return 'metadata';
  }

  if (block.actions.length > 0 && block.actions.every((action) => action.type === 'button_definition')) {
    return 'metadata';
  }

  return 'execution';
}

function resolveEntryBlock(blocks) {
  if (blocks.START?.blockType === 'execution' && hasExecutableActions(blocks.START)) {
    return 'START';
  }

  if (blocks.INICIAR?.blockType === 'execution' && hasExecutableActions(blocks.INICIAR)) {
    return 'INICIAR';
  }

  const firstExecutableBlock = Object.values(blocks).find((block) => block.blockType === 'execution' && hasExecutableActions(block));

  return firstExecutableBlock ? firstExecutableBlock.name : null;
}

function collectGotoTargets(action, targets = []) {
  if (!action) {
    return targets;
  }

  if (action.type === 'goto' && action.target) {
    targets.push({
      target: normalizeLabelName(action.target),
      line: action.line,
      raw: action.raw,
    });
  }

  ['action', 'trueAction', 'falseAction'].forEach((key) => {
    if (action[key]) {
      collectGotoTargets(action[key], targets);
    }
  });

  ['then', 'else'].forEach((key) => {
    if (Array.isArray(action[key])) {
      action[key].forEach((childAction) => collectGotoTargets(childAction, targets));
    }
  });

  return targets;
}

function validateGotoTargets(blocks) {
  const labels = new Set(Object.keys(blocks));

  return Object.values(blocks).flatMap((block) => {
    return (block.actions || [])
      .flatMap((action) => collectGotoTargets(action))
      .filter((gotoTarget) => !labels.has(gotoTarget.target))
      .map((gotoTarget) => ({
        type: 'diagnostic',
        level: 'warning',
        code: 'GOTO_TARGET_NOT_FOUND',
        message: 'GOTO apunta a un label que no existe.',
        target: gotoTarget.target,
        line: gotoTarget.line,
        raw: gotoTarget.raw,
      }));
  });
}

export function analyzeBlocks(ast, ir) {
  const nextIr = cloneIr(ir);
  const blocks = {};
  const logicByLine = createLineActionMap(nextIr);
  const menuByLine = new Map((nextIr.menus || []).map((menu) => [menu.line, menu]));
  const menuActionLines = new Set(
    (nextIr.menus || []).flatMap((menu) => {
      return (menu.items || menu.options || []).flatMap((item) => item.actionLines || []).filter(Boolean);
    })
  );
  const blockDiagnostics = [];
  let activeBlock = null;

  (ast.nodes || []).forEach((node) => {
    if (node.type === 'label') {
      const name = normalizeLabelName(node.name);
      blocks[name] = blocks[name] || {
        name,
        line: node.line,
        raw: node.raw,
        nodes: [],
        actions: [],
      };
      activeBlock = blocks[name];
      return;
    }

    if (!activeBlock) {
      return;
    }

    activeBlock.nodes.push(node);
  });

  Object.values(blocks).forEach((block) => {
    const result = groupActionSequence(block.nodes, 0, logicByLine, {
      insideIf: false,
      stopOnIfBoundary: false,
      menuByLine,
      menuActionLines,
    });

    block.actions = result.actions;
    blockDiagnostics.push(...result.diagnostics);
    delete block.nodes;
  });

  Object.values(blocks).forEach((block) => {
    block.blockType = inferBlockType(block);
  });

  nextIr.logicFlat = nextIr.logic || [];
  nextIr.blocks = blocks;
  nextIr.entryBlock = resolveEntryBlock(blocks);
  nextIr.diagnostics = [...(nextIr.diagnostics || []), ...blockDiagnostics, ...validateGotoTargets(blocks)];

  return nextIr;
}
